import { useTranslation } from 'react-i18next';
import { CheckCircle, CreditCard, Clock } from '@phosphor-icons/react';

import type { OnboardingDraft, OnboardingStepProps } from '../types';

// Step 4 of onboarding. Two large radio-style tiles let the user choose
// between hooking up Stripe Connect now (so they can issue invoices right
// after onboarding) or skipping and setting it up later from Settings →
// Billing. The choice gates the wizard's Slutför button; the actual Stripe
// redirect happens in Onboarding.tsx's submit handler AFTER createOrganization
// so we never strand the user with a half-created org.

export function Payments({ draft, update }: OnboardingStepProps) {
  const { t } = useTranslation('common');
  const selected = draft.connectStripe;

  return (
    <div className="form" style={{ gap: '1rem' }}>
      <p>{t('onboarding.payments.body')}</p>

      <div
        role="radiogroup"
        aria-label={t('onboarding.payments.title')}
        style={{ display: 'grid', gap: '0.75rem', gridTemplateColumns: '1fr' }}
        className="onboarding-payments__tiles"
      >
        <Tile
          icon={<CreditCard weight="bold" size={22} aria-hidden />}
          title={t('onboarding.payments.connect.title')}
          body={t('onboarding.payments.connect.body')}
          selected={selected === true}
          onSelect={() => update({ connectStripe: true })}
        />
        <Tile
          icon={<Clock weight="bold" size={22} aria-hidden />}
          title={t('onboarding.payments.skip.title')}
          body={t('onboarding.payments.skip.body')}
          selected={selected === false}
          onSelect={() => update({ connectStripe: false })}
        />
      </div>

      {selected === true && (
        <p
          className="form__hint"
          style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}
        >
          <CheckCircle weight="fill" size={16} aria-hidden style={{ flexShrink: 0 }} />
          <span>{t('onboarding.payments.connect.redirectHint')}</span>
        </p>
      )}
    </div>
  );
}

function Tile({
  icon,
  title,
  body,
  selected,
  onSelect,
}: {
  icon: React.ReactNode;
  title: string;
  body: string;
  selected: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      onClick={onSelect}
      style={{
        textAlign: 'left',
        display: 'flex',
        gap: '0.75rem',
        alignItems: 'flex-start',
        padding: '1rem 1.1rem',
        borderRadius: 'var(--radius-lg, 12px)',
        border: selected
          ? '2px solid var(--color-primary, var(--lime))'
          : '1px solid var(--color-border, rgba(0,0,0,0.12))',
        background: selected
          ? 'var(--color-accent-soft, rgba(190,224,52,0.12))'
          : 'var(--color-bg, transparent)',
        cursor: 'pointer',
        transition: 'border-color 120ms ease, background 120ms ease',
      }}
    >
      <span
        aria-hidden
        style={{
          flexShrink: 0,
          marginTop: '0.15rem',
          color: 'var(--color-fg)',
        }}
      >
        {icon}
      </span>
      <span style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
        <span style={{ fontWeight: 600 }}>{title}</span>
        <span style={{ color: 'var(--color-fg-muted)', fontSize: '0.875rem' }}>
          {body}
        </span>
      </span>
    </button>
  );
}

export const paymentsIsValid = (draft: OnboardingDraft): boolean =>
  draft.connectStripe !== null;
